import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import {
  useGoogleMapsStateUpdater,
  useGoogleMapsStateValue,
} from '@atom/components/common/map/hooks/googleMapsStateHook';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Icon, IconButton, Menu } from '@atom/mui';
import {
  MapAreaUnit,
  MapData,
  MapDistanceUnit,
  MapShapeType,
} from '@atom/types/map';
import {
  getMapAreaUnitLabel,
  getMapDistanceUnitLabel,
  getShapeArea,
  getShapeDistance,
  getShapeRadius,
} from '@atom/utilities/mapShapeUtilities';

import classes from './mapStats.module.css';

const { MenuItem } = Menu;

const styles = {
  icon: {
    height: '20px',
    width: '20px',
    fontSize: '20px',
  },
  helpIconButton: {
    padding: '0',
  },
};

const distanceUnitOptions = [
  MapDistanceUnit.MILES,
  MapDistanceUnit.FEET,
  MapDistanceUnit.YARDS,
  MapDistanceUnit.METERS,
];

const areaUnitOptions = [
  MapAreaUnit.MILES,
  MapAreaUnit.FEET,
  MapAreaUnit.YARDS,
  MapAreaUnit.ACRES,
  MapAreaUnit.METERS,
];

enum MessageType {
  MEASURE = 'MEASURE',
  POLYGON = 'POLYGON',
  NO_ITEM = 'NO_ITEM',
  ZOOM = 'ZOOM',
  NONE = 'NONE',
}

interface Message {
  message: string;
  messageStyle: string;
}

const messages: Record<MessageType, Message> = {
  [MessageType.POLYGON]: {
    message: 'Draw an area now.',
    messageStyle: classes.green,
  },
  [MessageType.MEASURE]: {
    message: 'Click points on the map to measure distance.',
    messageStyle: '',
  },
  [MessageType.NO_ITEM]: {
    message: 'No items to show.',
    messageStyle: classes.orange,
  },
  [MessageType.ZOOM]: {
    message: 'Please zoom closer on the map to view items.',
    messageStyle: classes.orange,
  },
  [MessageType.NONE]: { message: '', messageStyle: classes.hidden },
};

interface Props {
  data: MapData;
  loading: boolean;
}

const MapStats = ({ data, loading }: Props) => {
  const dispatch = useGoogleMapsStateUpdater();
  const { drawingMode, shape } = useGoogleMapsStateValue();

  const {
    clusters = [],
    workOrders = [],
    users = [],
    assets = [],
    workOrdersCount,
    assetsCount,
    usersCount,
  } = data;

  const [open, setOpen] = useState<boolean>(false);
  const [mode, setMode] = useState<MapShapeType>(drawingMode);
  const [areaUnit, setAreaUnit] = useState<MapAreaUnit>(MapAreaUnit.FEET);
  const [distanceUnit, setDistanceUnit] = useState<MapDistanceUnit>(
    MapDistanceUnit.FEET,
  );
  const [radiusUnit, setRadiusUnit] = useState<MapDistanceUnit>(
    MapDistanceUnit.FEET,
  );

  useEffect(() => {
    if (drawingMode) {
      setMode(drawingMode);
    }
  }, [drawingMode]);

  const isDrawing =
    (mode === MapShapeType.CIRCLE ||
      mode === MapShapeType.POLYGON ||
      mode === MapShapeType.POLYLINE) &&
    !shape;

  useEffect(() => {
    setOpen(!!shape || isDrawing);
  }, [shape, isDrawing]);

  const getLabel = () => {
    switch (mode) {
      case MapShapeType.POLYLINE: {
        return 'Measure';
      }
      case MapShapeType.POLYGON: {
        return 'Draw Polygon Area';
      }
      case MapShapeType.CIRCLE: {
        return 'Draw Radius Area';
      }
      default: {
        return '';
      }
    }
  };

  const isMeasureTool = mode === MapShapeType.POLYLINE;
  const totalItems = workOrders.length + users.length + assets.length;
  const label = getLabel();
  const tooltipText =
    'Distance and area values are rounded to the nearest 3 decimal places.';

  const preferences = usePreferences();
  const enableBulkImplementation = R.pathOr(
    false,
    ['workOrders', 'createBulkWorkFromMap'],
    preferences,
  );
  const showContent = !loading && !isDrawing;

  const getMessageData = (): Message => {
    if (isMeasureTool) {
      return messages[MessageType.MEASURE];
    }

    if (enableBulkImplementation && totalItems === 0 && clusters.length > 0) {
      return messages[MessageType.ZOOM];
    }

    if (enableBulkImplementation && totalItems === 0 && clusters.length <= 0) {
      return messages[MessageType.NO_ITEM];
    }

    if (isDrawing || loading) {
      return messages[MessageType.POLYGON];
    }

    return messages[MessageType.NONE];
  };

  const { message, messageStyle } = getMessageData();

  const formatNumber = (value: number) => {
    return Number(value).toLocaleString('en-US', {
      maximumFractionDigits: 3,
      minimumFractionDigits: 3,
    });
  };

  const distance = formatNumber(getShapeDistance(shape, distanceUnit));
  const distanceUnitLabel = getMapDistanceUnitLabel(distanceUnit);

  const radius = formatNumber(getShapeRadius(shape, radiusUnit));
  const radiusUnitLabel = getMapDistanceUnitLabel(radiusUnit);

  const area = formatNumber(getShapeArea(shape, areaUnit));
  const areaUnitLabel = getMapAreaUnitLabel(areaUnit);

  const handleClose = useCallback(() => {
    setOpen(false);
    setMode(null);
    dispatch({ type: 'SET', payload: { key: 'shape', data: null } });
    dispatch({ type: 'SET', payload: { key: 'drawingMode', data: null } });
  }, [dispatch]);

  const content = (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.titleContent}>
          <div className={classes.mainTitle}>{label}</div>
          <IconButton tooltip={tooltipText} style={styles.helpIconButton}>
            <Icon style={styles.icon}>help</Icon>
          </IconButton>
        </div>
        <IconButton onClick={handleClose} edge="end">
          <Icon>close</Icon>
        </IconButton>
      </div>
      <div className={clsx(classes.messageRow, messageStyle)}>
        <div>{message}</div>
      </div>
      {showContent && (
        <>
          <div className={classes.contentRow}>
            <div className={classes.rowTitle}>
              {mode === MapShapeType.CIRCLE
                ? 'Circumference'
                : mode === MapShapeType.POLYGON
                  ? 'Perimeter'
                  : 'Distance'}
            </div>
            <div className={classes.rowValue}>
              <div>{`${distance} ${distanceUnitLabel}`} </div>
              <Menu
                icon={<Icon>arrow_drop_down</Icon>}
                IconButtonProps={{ size: 'small' }}
              >
                {distanceUnitOptions.map(option => {
                  const optionLabel = getMapDistanceUnitLabel(option);

                  return (
                    <MenuItem
                      key={optionLabel}
                      onClick={() => setDistanceUnit(option)}
                      selected={option === distanceUnit}
                    >
                      <div>{optionLabel}</div>
                    </MenuItem>
                  );
                })}
              </Menu>
            </div>
          </div>
          {mode === MapShapeType.CIRCLE && (
            <div className={classes.contentRow}>
              <div className={classes.rowTitle}>Radius</div>
              <div className={classes.rowValue}>
                <div>{`${radius} ${radiusUnitLabel}`} </div>
                <Menu
                  icon={<Icon>arrow_drop_down</Icon>}
                  IconButtonProps={{ size: 'small' }}
                >
                  {distanceUnitOptions.map(option => {
                    const optionLabel = getMapDistanceUnitLabel(option);

                    return (
                      <MenuItem
                        key={optionLabel}
                        onClick={() => setRadiusUnit(option)}
                        selected={option === radiusUnit}
                      >
                        <div>{optionLabel}</div>
                      </MenuItem>
                    );
                  })}
                </Menu>
              </div>
            </div>
          )}
          {!isMeasureTool && (
            <>
              <div className={classes.contentRow}>
                <div className={classes.rowTitle}>Area</div>
                <div className={classes.rowValue}>
                  <div>{`${area} ${areaUnitLabel}`} </div>
                  <Menu
                    icon={<Icon>arrow_drop_down</Icon>}
                    IconButtonProps={{ size: 'small' }}
                  >
                    {areaUnitOptions.map(option => {
                      const optionLabel = getMapAreaUnitLabel(option);

                      return (
                        <MenuItem
                          key={optionLabel}
                          onClick={() => setAreaUnit(option)}
                          selected={option === areaUnit}
                        >
                          <div>{optionLabel}</div>
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </div>
              </div>
              <div className={classes.contentRow}>
                <div className={classes.rowTitle}>Inventory Items</div>
                <div>{assetsCount}</div>
              </div>
              <div className={classes.contentRow}>
                <div className={classes.rowTitle}>Work</div>
                <div>{workOrdersCount}</div>
              </div>
              <div className={classes.contentRow}>
                <div className={classes.rowTitle}>Team Members</div>
                <div>{usersCount}</div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

  return <div className={classes.wrapper}>{open && content}</div>;
};

export default MapStats;
